import { toLatinDigits, toPersianDigits } from './digits.js';

/**
 * Money rules (BR-01, ADR-014):
 * - Amounts are integer RIAL, stored as Postgres `bigint`, handled as `bigint` in code.
 * - On the wire they are decimal strings ("12500000000"), never JavaScript numbers.
 * - TOMAN exists only in presentation (1 toman = 10 rial).
 */

export const RIAL_MIN = -9223372036854775808n;
export const RIAL_MAX = 9223372036854775807n;

const RIAL_STRING = /^-?(0|[1-9][0-9]*)$/;

export function isRialString(value: string): boolean {
  if (!RIAL_STRING.test(value) || value === '-0') return false;
  const n = BigInt(value);
  return n >= RIAL_MIN && n <= RIAL_MAX;
}

export function parseRialString(value: string): bigint {
  if (!isRialString(value)) {
    throw new RangeError('Invalid rial amount string');
  }
  return BigInt(value);
}

export function toRialString(value: bigint): string {
  if (value < RIAL_MIN || value > RIAL_MAX) {
    throw new RangeError('Rial amount out of bigint range');
  }
  return value.toString();
}

function floorDiv(a: bigint, b: bigint): bigint {
  const q = a / b;
  return a % b !== 0n && a < 0n !== b < 0n ? q - 1n : q;
}

/** Rial → toman, rounding halves towards +infinity (same as the legacy `Math.round(n / 10)`). */
export function rialToToman(rial: bigint): bigint {
  return floorDiv(rial + 5n, 10n);
}

/** Display a rial amount as toman with Persian digits and thousands separators, e.g. `۱۲,۵۰۰`. */
export function formatToman(rial: bigint | string): string {
  const value = typeof rial === 'string' ? parseRialString(rial) : rial;
  const toman = rialToToman(value);
  const negative = toman < 0n;
  const digits = (negative ? -toman : toman).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (negative ? '-' : '') + toPersianDigits(digits);
}

/**
 * Parses a toman amount typed by a user (Persian/Arabic/Latin digits, optional separators,
 * at most one fractional digit because 1 toman = 10 rial) and returns rial.
 * Returns null for empty or malformed input. Never uses floating point.
 */
export function parseTomanInput(input: string): bigint | null {
  const normalized = toLatinDigits(input)
    .trim()
    .replace(/[\s,٬،]/g, '')
    .replace(/٫/g, '.');
  const match = /^(-?)(\d+)(?:\.(\d))?$/.exec(normalized);
  if (!match) return null;
  const [, sign, whole, fraction] = match;
  const rial = BigInt(whole as string) * 10n + BigInt(fraction ?? '0');
  const signed = sign === '-' ? -rial : rial;
  if (signed < RIAL_MIN || signed > RIAL_MAX) return null;
  return signed;
}

/** `n / d` rounded half away from zero (`round_half_up` of the spec; `d` must be positive). */
export function divideRoundHalfUp(n: bigint, d: bigint): bigint {
  if (d <= 0n) throw new RangeError('divisor must be positive');
  const negative = n < 0n;
  const abs = negative ? -n : n;
  const q = (abs * 2n + d) / (2n * d);
  return negative ? -q : q;
}

/** A share of an amount in basis points (1 bp = 0.01%), e.g. commission or practitioner share. */
export function basisPointsOf(amountRial: bigint, bp: number): bigint {
  if (!Number.isInteger(bp) || bp < 0 || bp > 10_000) throw new RangeError('basis points must be 0..10000');
  return divideRoundHalfUp(amountRial * BigInt(bp), 10_000n);
}

/** Cost of a quantity in thousandths (see quantity.ts) at a unit price, rounded to the rial. */
export function quantityCostRial(quantityMilli: bigint, unitCostRial: bigint): bigint {
  return divideRoundHalfUp(quantityMilli * unitCostRial, 1000n);
}
